import RFButton from "@/components/RFButton";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import PhoneAndEmailController from "./PhoneAndEmailController";
import { checkIsPhone, validateValidateCode } from "@/utils/validate";
import LabelText from "./LabelText";
import ErrorText from "./ErrorText";
import Toast from "@/components/Toast";
import * as LoginServices from "@/service/login.services";
import { RegisterParams } from "@/interfaces/loginTypes";
import rfStore from "@/utils/rfStore";
import Script from "next/script";
import { useTranslation } from "next-i18next";
import Input from "@/components/Input";
import Button from "@/components/Button";
import { setAuthInfo } from "@/utils/storage";

interface IFormState {
	identity: string;
	code: string;
}
const COUNT_TOTAL = 60;

interface Props {
	onLoginSuccess: () => void;
}

export default function Login(props: Props) {
	const { t } = useTranslation("login");
	const [loading, setLoading] = useState(false);
	const { control, handleSubmit, formState, watch } = useForm<IFormState>({
		mode: "onChange",
		defaultValues: {
			identity: "",
			code: "",
		},
	});
	const { errors, isValid } = formState;
	const watchIdentity = watch("identity");

	const identityType = useMemo(() => {
		return checkIsPhone(watchIdentity) ? 1 : 2;
	}, [watchIdentity]);

	const widgetId = useRef(-1);
	const timer = useRef<any>();
	const [count, setCount] = useState(COUNT_TOTAL);
	const [sendCodeDisabled, setSendCodeDisabled] = useState(false);

	useEffect(() => {
		const rfState = rfStore.getRFState();
		if (rfState) {
			rfStore.setRFState({
				...rfState,
				region: "CN",
			});
		}
	}, []);
	// 倒计时处理
	useEffect(() => {
		if (!sendCodeDisabled) {
			return;
		}
		timer.current = setInterval(() => {
			if (count === 0) {
				clearTimeout(timer.current);
				setSendCodeDisabled(false);
				setCount(COUNT_TOTAL);
				return;
			}
			setCount(count - 1);
		}, 1000);

		return () => {
			if (timer.current) {
				clearTimeout(timer.current);
			}
		};
	}, [count, sendCodeDisabled]);

	const shouldClickSendCodeBtnDisabled = useMemo(() => {
		if (sendCodeDisabled) return true;

		return !!errors.identity?.message;
	}, [errors.identity?.message, sendCodeDisabled]);

	const handleSendCode = () => {
		if (shouldClickSendCodeBtnDisabled) return;

		if (widgetId.current > -1) {
			window.grecaptcha.enterprise.reset(widgetId);
			return;
		}
		renderCaptchaWidget();
	};

	const renderCaptchaWidget = () => {
		const SITE_KEY = process.env.NEXT_PUBLIC_CAPTCHA_KEY;
		const opt_widget_id = window.grecaptcha.enterprise.render("g_recaptcha", {
			sitekey: SITE_KEY,
			theme: "light",
			callback(securityToken: string) {
				sendVerifiedCode(securityToken);
			},
		});
		widgetId.current = opt_widget_id;
	};
	const sendVerifiedCode = async (securityToken: string) => {
		setSendCodeDisabled(true);

		try {
			await LoginServices.sendVerifiedCode(identityType, watchIdentity, securityToken);
			Toast.success(t("send-success"));
		} catch (error) {
			let message;
			if (error instanceof Error) message = error.message;
			else message = String(error);
			Toast.fail(message);
		}
	};

	const onSubmit: SubmitHandler<IFormState> = async (data) => {
		const type = identityType;
		const identity = data.identity;

		setLoading(true);
		try {
			const resData = await LoginServices.validateCodeIsSafety(type, identity, data.code);

			if (!resData) return;
			const { accessToken } = resData;
			const isLogin = await LoginServices.hasRegister(identity, type);
			let res = { token: "", userId: "" };

			if (isLogin) {
				res = (await LoginServices.login({
					type: 3,
					identity,
					accessToken,
				})) as any;
			} else {
				const registerParams: RegisterParams = {
					type: 1,
					accessToken,
					region: "CN",
				};

				res = (await LoginServices.register(registerParams)) as any;
			}

			const { token, userId } = res;
			const _rfStore = rfStore.getRFState();
			if (_rfStore) {
				rfStore.setRFState({
					..._rfStore,
					userId,
					token,
				});
			}

			setAuthInfo(userId, token, "CN");

			props.onLoginSuccess();
		} catch (error) {
			let message;
			if (error instanceof Error) message = error.message;
			else message = String(error);
			Toast.fail(message);
		} finally {
			setLoading(false);
		}
	};

	const sendCodeText = () => {
		if (!sendCodeDisabled) {
			return t("buttonText");
		}

		const timeText = count === 0 ? "" : count < 10 ? "0" + count : count;
		const text = t("resetSendText");
		return `${text as string}(${String(timeText)})`;
	};

	return (
		<>
			<Script src="https://www.recaptcha.net/recaptcha/enterprise.js"></Script>

			<div className="w-[304px]">
				<div className="mt-4 text-center">
					<p className="text-xl font-bold text-[#19003E]">Hi Rocker!</p>
					<p className="mt-2 text-sm font-normal text-black">{t("are-you-ready-for-a-different-journey")}</p>
				</div>

				<div className="mt-5 py-5">
					<LabelText text={t("phone-or-email-number-label")} />
					<PhoneAndEmailController
						defaultValue=""
						name="identity"
						control={control as any}
						error={errors.identity?.message !== undefined}
						helperText={errors.identity?.message || ""}
					/>
					{errors.identity?.message ? <ErrorText text={errors.identity.message} /> : null}

					<div className="h-4"></div>

					<LabelText text={t("placeholderTextWithCode")} />
					<Controller
						name="code"
						control={control}
						rules={{
							required: t("validateCodeMsg"),
							validate: {
								checkPhoneNumber: (v) => {
									return validateValidateCode(v) || t("validateCodeMsg");
								},
							},
						}}
						render={({ field }) => (
							<Input
								{...field}
								placeholder={t("placeholderTextWithCode")}
								aria-label={t("placeholderTextWithCode")}
								id="phone_email"
								trailing={
									<div className="flex items-center">
										<span className="ml-4 h-5 w-[1px] flex-shrink-0 bg-light"></span>
										<Button
											size="sm"
											disabled={shouldClickSendCodeBtnDisabled}
											onClick={handleSendCode}
											className="text-sm"
										>
											{sendCodeText()}
										</Button>
									</div>
								}
							/>
						)}
					/>
					{errors.code?.message ? <ErrorText text={errors.code.message} /> : null}
				</div>

				<div id="g_recaptcha" className="my-2"></div>

				<div className="px-4">
					<RFButton
						type="primary"
						loading={loading}
						disabled={!isValid || loading}
						onClick={handleSubmit(onSubmit)}
						block
					>
						{t("loginBtnText")}
					</RFButton>
				</div>
			</div>
		</>
	);
}
